import type { TabId } from "./DetailTabs";
import TranscriptTab from "./TranscriptTab";
import SummaryTab from "./SummaryTab";
import MetadataTab from "./MetadataTab";
import type { Transcription } from "../../../types/transcription/Transcription";
import type { InteractionSummary } from "../../../types/summary/Summary";
import type { TranscribeAndSummarizePhase } from "../../../hooks/useSummary";

interface DetailTabContentProps {
  activeTab: TabId;
  customerId: string;
  interactionId: string;
  interactionType: string;
  hasMedia: boolean;
  transcript?: Transcription | null;
  transcriptLoading: boolean;
  currentTimeSeconds?: number;
  summary?: InteractionSummary | null;
  summaryLoading: boolean;
  metadata?: Record<string, unknown>;
  onSummaryRequested?: () => void;
  onTranscribeAndSummarize: (templateId: string) => void;
  transcribeSummarizePhase?: TranscribeAndSummarizePhase;
}

/**
 * Renders the transcript / summary / metadata tab body for the active tab.
 * Shared by DetailPane and the pop-out surfaces so the tab wiring lives in one
 * place rather than being duplicated across each surface.
 */
export default function DetailTabContent({
  activeTab,
  customerId,
  interactionId,
  interactionType,
  hasMedia,
  transcript,
  transcriptLoading,
  currentTimeSeconds,
  summary,
  summaryLoading,
  metadata,
  onSummaryRequested,
  onTranscribeAndSummarize,
  transcribeSummarizePhase,
}: DetailTabContentProps) {
  return (
    <>
      {activeTab === "transcript" && (
        <TranscriptTab
          customerId={customerId}
          interactionId={interactionId}
          interactionType={interactionType}
          hasMedia={hasMedia}
          transcript={transcript ?? undefined}
          isLoading={transcriptLoading}
          currentTimeSeconds={currentTimeSeconds}
          onTranscribeAndSummarize={onTranscribeAndSummarize}
          transcribeSummarizePhase={transcribeSummarizePhase}
        />
      )}
      {activeTab === "summary" && (
        <SummaryTab
          customerId={customerId}
          interactionId={interactionId}
          summary={summary ?? undefined}
          isLoading={summaryLoading}
          hasTranscript={!!transcript}
          hasMedia={hasMedia}
          interactionType={interactionType}
          onSummaryRequested={onSummaryRequested}
          onTranscribeAndSummarize={onTranscribeAndSummarize}
          transcribeSummarizePhase={transcribeSummarizePhase}
        />
      )}
      {activeTab === "metadata" && <MetadataTab metadata={metadata ?? {}} />}
    </>
  );
}
